import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus,
  Trash2,
  X,
  Save,
  ArrowLeft,
  UserCircle,
  Workflow as WorkflowIcon
} from 'lucide-react';
import { fetchTemplate, updateTemplate } from '../../../services/workflowTemplateService.ts';
import { fetchApprovers } from '../../../services/approverService.ts';
import { getAllUsers } from '../../../services/userService.ts';
import { useNotify } from 'react-admin';

interface ApprovalStep {
  id?: string;
  stepNo: number;
  Name: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
  userId?: string;
  title?: string;
  departmentTitle?: string;
  departmentTitleId?: string;
  selectorType?: 'user' | 'departmentTitle';
  selectorValue?: string;
}

interface ApprovalTemplate {
  id?: string;
  Name: string;
  docType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdDate: string;
  createdBy: number;
  lastModified: string;
  usageCount: number;
}

type ApproverLike = Approver | string | number | { id?: string | number; userId?: string | number; name?: string; userName?: string; role?: string; title?: string; email?: string; departmentTitle?: string; departmentTile?: string; departmentTitleId?: string | number; departmentTileId?: string | number; selectorType?: 'user' | 'departmentTitle'; selectorValue?: string };

const buildApproverMap = (pool: Approver[]) => new Map(pool.map(approver => [approver.id, approver]));

const buildDepartmentSelectorId = (title: string, departmentTitleId?: string) => {
  const normalizedId = String(departmentTitleId || '').trim();
  return normalizedId ? `deptid:${normalizedId}` : `dept:${title.trim()}`;
};

const normalizeApprover = (
  item: ApproverLike,
  poolMap: Map<string, Approver>,
  userNameMap: Map<string, string>
): Approver => {
  if (item && typeof item === 'object') {
    const raw = item as { id?: string | number; userId?: string | number; name?: string; userName?: string; role?: string; title?: string; email?: string; departmentTitle?: string; departmentTile?: string; departmentTitleId?: string | number; departmentTileId?: string | number; selectorType?: 'user' | 'departmentTitle'; selectorValue?: string };

    if (raw.selectorType === 'departmentTitle' || String(raw.id || '').startsWith('dept:') || String(raw.id || '').startsWith('deptid:')) {
      const rawId = String(raw.id || '');
      const title = String(raw.departmentTitle || raw.departmentTile || raw.name || raw.selectorValue || '').trim();
      const departmentTitleId = String(
        raw.departmentTitleId ??
        raw.departmentTileId ??
        (rawId.startsWith('deptid:') ? rawId.replace(/^deptid:/, '') : '')
      ).trim();
      return {
        id: String(raw.id || buildDepartmentSelectorId(title, departmentTitleId)),
        userId: '',
        name: title,
        role: 'DepartmentTitle',
        email: '',
        departmentTitle: title,
        departmentTitleId,
        selectorType: 'departmentTitle',
        selectorValue: departmentTitleId || title
      };
    }

    // Prefer userId over id for user lookup
    const userId = raw.userId != null ? String(raw.userId) : (raw.id != null ? String(raw.id) : '');
    const id = raw.id != null ? String(raw.id) : userId;
    
    // Try to get name from multiple sources
    const rawName = raw.name || raw.userName;
    const mappedName = userId ? userNameMap.get(userId) : undefined;
    
    const fromPool = (id ? poolMap.get(id) : undefined) || (userId ? poolMap.get(userId) : undefined);
    if (fromPool) {
      return {
        ...fromPool,
        userId: userId || fromPool.userId,
        id: id || fromPool.id,
        name: rawName || mappedName || fromPool.name,
        title: raw.title || fromPool.title,
        role: raw.role || fromPool.role,
        email: raw.email || fromPool.email,
        departmentTitle: raw.departmentTitle || raw.departmentTile || fromPool.departmentTitle || '',
        departmentTitleId: String(raw.departmentTitleId ?? raw.departmentTileId ?? fromPool.departmentTitleId ?? '').trim(),
        selectorType: 'user',
        selectorValue: userId || fromPool.selectorValue
      };
    }
    return {
      id,
      userId,
      name: rawName || mappedName || id || '',
      title: raw.title || '',
      role: raw.role || '-',
      email: raw.email || '',
      departmentTitle: raw.departmentTitle || raw.departmentTile || '',
      departmentTitleId: String(raw.departmentTitleId ?? raw.departmentTileId ?? '').trim(),
      selectorType: 'user',
      selectorValue: userId
    };
  }

  const id = item != null ? String(item) : '';
  const fromPool = id ? poolMap.get(id) : undefined;
  if (fromPool) {
    const mappedName = userNameMap.get(id);
    return {
      ...fromPool,
      userId: id,
      name: mappedName || fromPool.name,
      title: fromPool.title,
      selectorType: 'user',
      selectorValue: id
    };
  }
  return { id, userId: id, name: userNameMap.get(id) || id, role: '-', email: '', departmentTitle: '', departmentTitleId: '', selectorType: 'user', selectorValue: id };
};

const normalizeApproverList = (
  items: ApproverLike[] | undefined,
  poolMap: Map<string, Approver>,
  userNameMap: Map<string, string>
): Approver[] => {
  if (!items || items.length === 0) return [];
  return items.map(item => normalizeApprover(item, poolMap, userNameMap));
};

const needsApproverHydration = (items: ApproverLike[] | undefined): boolean => {
  if (!items || items.length === 0) return false;
  return items.some(item => {
    if (!item) return true;
    if (typeof item !== 'object') return true;
    const raw = item as { id?: string | number; userId?: string | number; name?: string; userName?: string; selectorType?: 'user' | 'departmentTitle' };
    if (raw.selectorType === 'departmentTitle' || String(raw.id || '').startsWith('dept:')) return false;
    // If we have userName but not name, we need to hydrate it
    if (raw.userName && !raw.name) return true;
    // Check if we have a display name that's not just a numeric ID
    const displayName = raw.name || raw.userName;
    if (!displayName) return true; // Need hydration if no name
    // Check if name looks like just a numeric ID (userId/id)
    if (/^\d+$/.test(String(displayName).trim())) return true;
    return false;
  });
};

const createDepartmentTitleApprover = (title: string, departmentTitleId?: string): Approver => {
  const normalized = title.trim();
  const normalizedId = String(departmentTitleId || '').trim();
  return {
    id: buildDepartmentSelectorId(normalized, normalizedId),
    userId: '',
    name: normalized,
    role: 'DepartmentTitle',
    email: '',
    departmentTitle: normalized,
    departmentTitleId: normalizedId,
    selectorType: 'departmentTitle',
    selectorValue: normalizedId || normalized
  };
};

const TemplateEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [userNameMap, setUserNameMap] = useState<Map<string, string>>(new Map());
  const [isLoadingApprovers, setIsLoadingApprovers] = useState(true);

  const [template, setTemplate] = useState<ApprovalTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [selectedDepartmentTitle, setSelectedDepartmentTitle] = useState<string>('all');
  const [approverKeyword, setApproverKeyword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();

  const departmentTitleOptions = useMemo(() => {
    const map = new Map<string, { title: string; id: string }>();
    approversPool.forEach(approver => {
      const title = String(approver.departmentTitle || '').trim();
      if (!title) return;
      const id = String(approver.departmentTitleId || '').trim();
      if (!map.has(title)) {
        map.set(title, { title, id });
      } else if (!map.get(title)?.id && id) {
        map.set(title, { title, id });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [approversPool]);

  const selectedDepartmentTitleId = useMemo(() => {
    if (selectedDepartmentTitle === 'all') return '';
    return departmentTitleOptions.find(option => option.title === selectedDepartmentTitle)?.id || '';
  }, [departmentTitleOptions, selectedDepartmentTitle]);

  const currentStep = useMemo(() => {
    if (!currentStepId || !template) return undefined;
    return template.steps.find(s => s.id === currentStepId);
  }, [currentStepId, template]);

  const filteredApprovers = useMemo(() => {
    const keyword = approverKeyword.trim().toLowerCase();
    return approversPool.filter(approver => {
      const matchesDepartment =
        selectedDepartmentTitle === 'all' ||
        (approver.departmentTitle || '') === selectedDepartmentTitle;

      if (!matchesDepartment) return false;
      if (!keyword) return true;

      const target = `${approver.name} ${approver.email} ${approver.departmentTitle || ''}`.toLowerCase();
      return target.includes(keyword);
    });
  }, [approverKeyword, approversPool, selectedDepartmentTitle]);

  const closeApproverModal = () => {
    setShowApproverModal(false);
    setCurrentStepId(null);
    setSelectedDepartmentTitle('all');
    setApproverKeyword('');
  };
  useEffect(() => {
    const loadTemplate = async () => {
      if (!id) {
        notify('Template ID is missing', { type: 'error' });
        navigate('/QmsWorkflowTemplate');
        return;
      }

      try {
        setIsLoading(true);
        const data = await fetchTemplate(id);
        setTemplate({
          ...data,
          docType: data.docType === 'Qms' ? 'QMS' : data.docType,
        });
      } catch (error) {
        console.error('Failed to load template:', error);
        notify('Failed to load template', { type: 'error' });
        navigate('/QmsWorkflowTemplate');
      } finally {
        setIsLoading(false);
      }
    };

    const loadApprovers = async () => {
      try {
        setIsLoadingApprovers(true);
        const data = await fetchApprovers();
        setApproversPool(data.map((approver: any) => ({
          ...approver,
          userId: String(approver.id || ''),
          selectorType: 'user',
          selectorValue: String(approver.id || '')
        })));
      } catch (error) {
        console.error('Failed to load users:', error);
        notify('Failed to load approvers', { type: 'error' });
      } finally {
        setIsLoadingApprovers(false);
      }
    };

    const loadUsers = async () => {
      try {
        const users = await getAllUsers();
        const map = new Map<string, string>();
        users.forEach((user: any) => {
          const userId = user?.id ?? user?.Id ?? user?.userId ?? user?.UserId;
          const userName = user?.userName ?? user?.username ?? user?.name ?? user?.Name;
          if (userId != null && userName) {
            map.set(String(userId), String(userName));
          }
        });
        setUserNameMap(map);
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };

    loadTemplate();
    loadApprovers();
    loadUsers();
  }, [id, navigate, notify]);

  useEffect(() => {
    const poolMap = buildApproverMap(approversPool);
    
    setTemplate(prev => {
      if (!prev) return prev;
      
      let changed = false;
      const updatedSteps = prev.steps.map(step => {
        const rawApprovers = step.approvers as unknown as ApproverLike[];
        if (!needsApproverHydration(rawApprovers)) {
          return step;
        }
        changed = true;
        return {
          ...step,
          approvers: normalizeApproverList(rawApprovers, poolMap, userNameMap)
        };
      });

      if (!changed) return prev;
      return { ...prev, steps: updatedSteps };
    });
  }, [approversPool, userNameMap]);

  const handleAddStep = () => {
    if (!template) return;

    const newStep: ApprovalStep = {
      id: `s${template.steps.length + 1}`,
      stepNo: template.steps.length + 1,
      Name: `Step ${template.steps.length + 1}`,
      stepType: 'single',
      approvers: [],
      approvalRule: 'all',
      timeLimit: 2,
      description: '',
      isOptional: false
    };
    setTemplate({
      ...template,
      steps: [...template.steps, newStep]
    });
  };

  const handleDeleteStep = (stepId: string) => {
    if (!template) return;

    const updatedSteps = template.steps
      .filter(s => s.id !== stepId)
      .map((s, idx) => ({ ...s, stepNo: idx + 1 }));
    setTemplate({
      ...template,
      steps: updatedSteps
    });
  };

  const updateStepField = (stepId: string, field: keyof ApprovalStep, value: any) => {
    if (!template) return;

    setTemplate({
      ...template,
      steps: template.steps.map(s =>
        s.id === stepId ? { ...s, [field]: value } : s
      )
    });
  };

  const handleAddApprover = (stepId: string, approver: Approver) => {
    if (!template) return;

    setTemplate({
      ...template,
      steps: template.steps.map(s => {
        if (s.id !== stepId) return s;
        const exists = s.approvers.some(a => a.id === approver.id);
        return exists ? s : { ...s, approvers: [...s.approvers, approver] };
      })
    });
  };

  const handleRemoveApprover = (stepId: string, approverId: string) => {
    if (!template) return;

    setTemplate({
      ...template,
      steps: template.steps.map(s =>
        s.id === stepId ? { ...s, approvers: s.approvers.filter(a => a.id !== approverId) } : s
      )
    });
  };

  const handleSubmit = async () => {
    if (!template) return;

    if (!template.Name.trim()) {
      notify('Please enter the template name', { type: 'error' });
      return;
    }

    try {
      setIsSubmitting(true);
      await updateTemplate(template);
      notify('Template updated successfully', { type: 'success' });
      navigate('/QmsWorkflowTemplate');
    } catch (error) {
      console.error('Failed to update template:', error);
      notify('Failed to update template', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="pb-6">
        <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
            <div className="h-7 bg-gray-200 rounded w-72"></div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          <p className="text-gray-600 mt-4 text-sm">Loading template...</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <div className="pb-6">
      {/* Header + Info Panel */}
      {/* Header + Basic Information 合并卡片 */}
      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div>
            <button
              onClick={() => navigate('/QmsWorkflowTemplate')}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">Edit Workflow Template</h2>
            <p className="text-sm text-slate-500 mt-1">Update the approval steps and approvers for your workflow</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/QmsWorkflowTemplate')}
              className="px-4 h-9 border border-gray-300 text-gray-700 rounded-[10px] hover:bg-gray-50 transition-colors text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        <div className="border-t border-gray-100 pt-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                <input
                  type="text"
                  value={template.Name}
                  onChange={(e) => setTemplate({ ...template, Name: e.target.value })}
                  placeholder="e.g., SOP Standard Approval Flow"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Type</label>
                <select
                  value={template.docType}
                  onChange={(e) => setTemplate({ ...template, docType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="QMS">QMS</option>
                  <option value="SOP">SOP</option>
                  <option value="Forms">Forms</option>
                  <option value="Policy">Policy</option>
                  <option value="SDS">SDS</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={template.description}
                onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                placeholder="Describe the purpose and scope of this workflow template..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Approval Steps */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Approval Steps</h3>
            <p className="text-sm text-slate-500 mt-0.5">{template.steps.length} step{template.steps.length !== 1 ? 's' : ''} configured</p>
          </div>
          <button
            onClick={handleAddStep}
            className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
          >
            <Plus className="size-4" />
            Add Step
          </button>
        </div>

        {template.steps.length === 0 ? (
          <div className="text-center py-14 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl">
            <WorkflowIcon className="size-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium mb-1">No approval steps yet</p>
            <p className="text-sm text-gray-400 mb-4">Add steps to define the approval workflow</p>
            <button
              onClick={handleAddStep}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
            >
              <Plus className="size-4" />
              Add First Step
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {template.steps.map((step, index) => (
              <div key={step.id}>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* Step Header */}
                  <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {step.stepNo}
                      </div>
                      <span className="font-semibold text-slate-800 text-sm">{step.Name || `Step ${step.stepNo}`}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        step.stepType === 'parallel' ? 'bg-purple-100 text-purple-800' :
                        step.stepType === 'conditional' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {step.stepType === 'parallel' ? 'Parallel' : step.stepType === 'conditional' ? 'Conditional' : 'Single'}
                      </span>
                    </div>
                    <button
                      onClick={() => step.id && handleDeleteStep(step.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="size-3.5" />
                      Delete
                    </button>
                  </div>

                  {/* Step Body */}
                  <div className="p-5">
                    <div className="grid grid-cols-2 gap-4 mb-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Step Name</label>
                        <input
                          type="text"
                          value={step.Name}
                          onChange={(e) => step.id && updateStepField(step.id, 'Name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Step Type</label>
                        <select
                          value={step.stepType}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm bg-gray-100 cursor-not-allowed"
                        >
                          <option value="parallel">Parallel Approval</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                        <textarea
                          value={step.description}
                          onChange={(e) => step.id && updateStepField(step.id, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>

                    {/* Approvers */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Approvers</label>
                        <button
                          onClick={() => {
                            if (step.id) {
                              setCurrentStepId(step.id);
                              setSelectedDepartmentTitle('all');
                              setApproverKeyword('');
                              setShowApproverModal(true);
                            }
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
                        >
                          <Plus className="size-3.5" />
                          Add Approver
                        </button>
                      </div>
                      {step.approvers.length === 0 ? (
                        <div className="text-center py-5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400">
                          No approvers assigned yet
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {/* Department Titles */}
                          {step.approvers.some(a => a.selectorType === 'departmentTitle') && (
                            <div>
                              <div className="text-xs font-semibold text-purple-600 mb-1.5 uppercase tracking-wide">Department Titles</div>
                              <div className="flex flex-wrap gap-2">
                                {step.approvers.filter(a => a.selectorType === 'departmentTitle').map(approver => (
                                  <div
                                    key={approver.id}
                                    className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm"
                                  >
                                    <UserCircle className="size-4 text-purple-500 flex-shrink-0" />
                                    <div>
                                      <div className="font-medium text-gray-900 text-sm">{approver.departmentTitle || approver.name}</div>
                                      <div className="text-xs text-purple-600">Dynamic by Department Title</div>
                                    </div>
                                    <button
                                      onClick={() => step.id && handleRemoveApprover(step.id, approver.id)}
                                      className="ml-1 text-gray-400 hover:text-red-600"
                                    >
                                      <X className="size-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Persons */}
                          {step.approvers.some(a => a.selectorType !== 'departmentTitle') && (
                            <div>
                              <div className="text-xs font-semibold text-blue-600 mb-1.5 uppercase tracking-wide">Persons</div>
                              <div className="flex flex-wrap gap-2">
                                {step.approvers.filter(a => a.selectorType !== 'departmentTitle').map(approver => (
                                  <div
                                    key={approver.id}
                                    className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm"
                                  >
                                    <UserCircle className="size-4 text-blue-500 flex-shrink-0" />
                                    <div>
                                      <div className="font-medium text-gray-900 text-sm">{approver.name || (approver as any).userName || approver.id}</div>
                                      <div className="text-xs text-gray-500">{approver.departmentTitle || approver.title || approver.role || '-'}</div>
                                    </div>
                                    <button
                                      onClick={() => step.id && handleRemoveApprover(step.id, approver.id)}
                                      className="ml-1 text-gray-400 hover:text-red-600"
                                    >
                                      <X className="size-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {index < template.steps.length - 1 && (
                  <div className="flex justify-center py-2">
                    <div className="w-0.5 h-5 bg-gray-300 rounded"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="flex justify-end gap-3 pb-2">
        <button
          onClick={() => navigate('/QmsWorkflowTemplate')}
          className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-[10px] hover:bg-gray-50 transition-colors text-sm font-semibold"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold disabled:opacity-50"
        >
          <Save className="size-4" />
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  onClick={closeApproverModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              {isLoadingApprovers ? (
                <div className="text-center text-gray-600">Loading users...</div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Department Title
                        </label>
                        <select
                          value={selectedDepartmentTitle}
                          onChange={(e) => setSelectedDepartmentTitle(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Department Titles</option>
                          {departmentTitleOptions.map(option => (
                            <option key={`${option.id}-${option.title}`} value={option.title}>{option.title}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Person
                        </label>
                        <input
                          type="text"
                          value={approverKeyword}
                          onChange={(e) => setApproverKeyword(e.target.value)}
                          placeholder="Search by name or email"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">
                        {filteredApprovers.length} people available
                      </p>
                      <button
                        type="button"
                        disabled={selectedDepartmentTitle === 'all' || !selectedDepartmentTitle || !!currentStep?.approvers.some(a => a.id === buildDepartmentSelectorId(selectedDepartmentTitle, selectedDepartmentTitleId))}
                        onClick={() => {
                          if (selectedDepartmentTitle && selectedDepartmentTitle !== 'all' && currentStepId) {
                            handleAddApprover(currentStepId, createDepartmentTitleApprover(selectedDepartmentTitle, selectedDepartmentTitleId));
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-md text-xs font-semibold hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="size-3.5" />
                        Save Department Title
                      </button>
                    </div>
                  </div>

                  {filteredApprovers.length === 0 ? (
                    <div className="text-center text-sm text-gray-500 py-10 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                      No user matched the selected department/person condition.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                      {filteredApprovers.map(approver => {
                        const isAlreadyAdded = currentStep?.approvers.some(a => a.id === approver.id || (a.userId && a.userId === approver.id));

                        return (
                          <button
                            key={approver.id}
                            onClick={() => {
                              if (!isAlreadyAdded && currentStepId) {
                                handleAddApprover(currentStepId, approver);
                              }
                            }}
                            disabled={isAlreadyAdded}
                            className={`p-4 border rounded-lg text-left transition-colors ${
                              isAlreadyAdded
                                ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                                : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <UserCircle className="size-10 text-gray-400" />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{approver.name || (approver as any).userName || approver.id}</div>
                                <div className="text-sm text-gray-600">{approver.departmentTitle || approver.title || approver.role || '-'}</div>
                                <div className="text-xs text-gray-500">{approver.email}</div>
                                <div className="mt-1 text-[11px] font-semibold text-blue-600">Person</div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeApproverModal}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEdit;
